import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Grid, Icon, LinearProgress, MenuItem, Modal, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { VForm, VTextField, useVForm } from "../../../shared/forms";
import { VMaskTextField } from "../../../shared/forms/VMaskTextField";
import { useToast } from "../../../shared/hooks";
import { FinancialService, ISetup } from "../../../shared/services/api/financial/FinancialService";
import { IPaymentSetup } from "../../../shared/services/api/financial/Types";
import { globalRemoveMask } from "../../../shared/utils";

type PaymentSetupFormData = {
	paymentDate: string;
	creditAmount: string;
	reason?: string;
	note?: string;
};

interface IModalPaymentSetupProps {
	id: string;
	clientName: string;
	dueDate: string;
	valueSetup: string;
	isOpenModal: boolean;
	isCloseModal?: () => void;
	widthModfy?: string;
	heightModfy?: string;
	enableBackdropClose?: boolean;
	isExecute?: boolean;
	onclickConfirm?: () => void;
	selectedSetups?: ISetup[];
}

export const ModalPaymentSetup: React.FC<IModalPaymentSetupProps> = ({
	id,
	clientName,
	dueDate,
	valueSetup,
	isCloseModal,
	onclickConfirm,
	isOpenModal,
	widthModfy,
	heightModfy,
	isExecute,
	selectedSetups,
}) => {
	const defaultReason = "Outros - manually";
	const [isLoading, setIsLoading] = useState(false);
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [note, setNote] = useState("");
	const [reason, setReason] = useState(defaultReason);
	const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
	const [processedCount, setProcessedCount] = useState(0);
	const [processingSummary, setProcessingSummary] = useState<{ success: number; error: number } | null>(null);

	const setupsToProcess = useMemo(
		() =>
			selectedSetups && selectedSetups.length > 0
				? selectedSetups
				: [
					{
						id,
						tenant_name: clientName,
						dueDate,
						raw_amount: valueSetup,
					} as ISetup,
				  ],
		[selectedSetups, id, clientName, dueDate, valueSetup]
	);
	const isBatchMode = setupsToProcess.length > 1;
	const currentSetup = setupsToProcess[Math.min(currentProcessingIndex, setupsToProcess.length - 1)];

	const style = {
		position: "absolute" as "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: widthModfy?.trim() ? widthModfy : "auto",
		height: heightModfy?.trim() ? heightModfy : "auto",
		minWidth: "650px",
		bgcolor: "background.paper",
		border: "1px solid #605e5e",
		boxShadow: 24,
		p: 4,
		borderRadius: "6px",
	};

	useEffect(() => {
		if (!isOpenModal) return;

		setNote("");
		setReason(defaultReason);
		setCurrentProcessingIndex(0);
		setProcessedCount(0);
		setProcessingSummary(null);
		formRef.current?.setData({
			paymentDate: "",
			creditAmount: parseFloat(valueSetup || "0").toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
			reason: defaultReason,
			note: "",
		});
	}, [isOpenModal, formRef, valueSetup]);

	const handlePaymentSetup = async (dados: PaymentSetupFormData) => {
		setIsLoading(true);
		setProcessedCount(0);
		setProcessingSummary(null);

		let successCount = 0;
		let errorCount = 0;
		const totalItems = setupsToProcess.length;

		for (let index = 0; index < totalItems; index++) {
			const setup = setupsToProcess[index];
			setCurrentProcessingIndex(index);
			const formattedPaymentDate = dados.paymentDate ? new Date(`${dados.paymentDate}T00:00:00`).toISOString() : new Date().toISOString();
			const creditAmount = Number(isBatchMode ? setup.raw_amount : globalRemoveMask(dados.creditAmount));

			const payload: IPaymentSetup = {
				paymentDate: formattedPaymentDate,
				creditAmount,
				reason,
				note,
			};

			const result = await FinancialService.PaymentSetup(setup.id, payload);
			if (result instanceof Error) {
				errorCount++;
			} else {
				successCount++;
			}

			setProcessedCount(index + 1);

			if (index < totalItems - 1) {
				await new Promise((resolve) => setTimeout(resolve, 400));
			}
		}

		setIsLoading(false);
		setProcessingSummary({ success: successCount, error: errorCount });

		toastHandler.present({
			type: errorCount === 0 ? "success" : successCount > 0 ? "warning" : "error",
			position: "top-right",
			messageOrContent: (
				<ToastContent
					title={"Processamento concluído"}
					content={`Sucesso: ${successCount} | Erros: ${errorCount}`}
				/>
			),
		});

		onclickConfirm?.();
		isCloseModal?.();
	};

	return (
		<div>
			{isOpenModal && (
				<Modal
					open={isOpenModal}
					aria-labelledby="modal-modal-title"
					aria-describedby="modal-modal-description"
					onClose={(_, reasonClose) => {
						if (reasonClose === "backdropClick" && isLoading) return;
						if (isLoading) return;
						isCloseModal?.();
					}}
				>
					<Box sx={style}>
						<DialogTitle style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 3 }}>
							<Typography id="modal-modal-title" variant="h5" color={"primary"}>
								{isBatchMode ? `Baixa manual em lote (${setupsToProcess.length})` : "Baixa manual de taxa de setup"}
							</Typography>
							{isBatchMode && (
								<Typography variant="body2" color="text.secondary">
									O motivo e a observação informados serão aplicados a todas as linhas selecionadas.
								</Typography>
							)}
						</DialogTitle>
						<DialogContent style={{ display: "flex", justifyContent: "center", padding: "2px", margin: 0 }}>
							<Box sx={{ width: "100%" }}>
								<Typography>
									<VForm
										id="formIdconfirm"
										placeholder="Your placeholder"
										onPointerEnterCapture={() => console.log("Pointer entered")}
										onPointerLeaveCapture={() => console.log("Pointer left")}
										ref={formRef}
										onSubmit={handlePaymentSetup}
									>
										<Box className="teste" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
											{isLoading && (
												<Grid item>
													<Typography variant="body2" color="primary" sx={{ mb: 1 }}>
														Processando {processedCount + 1} de {setupsToProcess.length}: {currentSetup?.tenant_name || clientName}
													</Typography>
													<LinearProgress variant="indeterminate" />
												</Grid>
											)}
											{processingSummary && !isLoading && (
												<Typography variant="body2" color="text.secondary">
													Último processamento: {processingSummary.success} sucesso(s) e {processingSummary.error} erro(s).
												</Typography>
											)}
											<Grid container direction="column" padding={1} spacing={2}>
												<Grid container item direction="row" spacing={1} sx={{ mb: 0 }}>
													<Grid item xs={2} sx={{ textAlign: "left" }}>
														Cliente :
													</Grid>
													<Grid item xs={8} sx={{ alignItems: "left" }}>
														<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
															{currentSetup?.tenant_name ?? clientName}
														</Typography>
													</Grid>
												</Grid>

												<Grid container item direction="row" spacing={1} sx={{ mb: 2 }}>
													<Grid item xs={2} sx={{ textAlign: "left" }}>
														Vencimento :
													</Grid>
													<Grid item xs={2} sx={{ alignItems: "left" }}>
														<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
															{moment(currentSetup?.dueDate ?? dueDate).format("L")}
														</Typography>
													</Grid>

													<Grid item xs={2} sx={{ textAlign: "right" }}>
														Valor :
													</Grid>
													<Grid item xs={3} sx={{ alignItems: "left" }}>
														<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
															{parseFloat(currentSetup?.raw_amount ?? valueSetup).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
														</Typography>
													</Grid>
												</Grid>

												<Divider />

												<Grid container item direction="row" spacing={2} mt={1}>
													<Grid item xs={4} sx={{ alignItems: "left" }}>
														<VTextField
															fullWidth
															label="Data Pagamento"
															name="paymentDate"
															disabled={isLoading}
															type="date"
														></VTextField>
													</Grid>
													{!isBatchMode && (
														<Grid item xs={4} sx={{ alignItems: "left" }}>
															<VMaskTextField
																fullWidth
																label="Valor Pago"
																name="creditAmount"
																disabled={isLoading}
																value={parseFloat(valueSetup).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
																mask="real"
															/>
														</Grid>
													)}
													{isBatchMode && (
														<Grid item xs={8} sx={{ display: "flex", alignItems: "center" }}>
															<Typography variant="body2" color="text.secondary">
																Cada linha será baixada com o valor original da respectiva taxa setup.
															</Typography>
														</Grid>
													)}
												</Grid>

												<Grid container item direction="row" spacing={2} mt={1}>
													<Grid item xs={12} md={8} lg={12} xl={12}>
														<VTextField
															fullWidth
															label="Motivo da Baixa manual"
															name="reason"
															disabled={isLoading}
															defaultValue={defaultReason}
															select
															onChange={(e) => setReason(e.target.value)}
														>
															<MenuItem value={"Erro no sistema - manually"}>Erro no sistema</MenuItem>
															<MenuItem value={"Pagamento direto - manually"}>Pagamento Direto Garantti</MenuItem>
															<MenuItem value={"Taxa Setup descontado/ajustado - manually"}>Boleto descontado/ajustado</MenuItem>
															<MenuItem value={"Outros - manually"}>Outros</MenuItem>
														</VTextField>
													</Grid>
												</Grid>

												<Grid container item direction="row" spacing={2}>
													<Grid item xs={12}>
														<VTextField
															fullWidth
															label="Descreva o pagamento"
															name="note"
															disabled={isLoading}
															multiline
															rows={4}
															onChange={(e) => setNote(e.target.value)}
														></VTextField>
													</Grid>
												</Grid>
											</Grid>
										</Box>
									</VForm>
								</Typography>
							</Box>
						</DialogContent>
						<DialogActions style={{ display: "flex", justifyContent: "center" }}>
							<Button onClick={isCloseModal} color="inherit" disableElevation variant={"contained"} startIcon={<Icon> arrow_back_icon</Icon>} disabled={isLoading}>
								Voltar
							</Button>
							{reason != "" ? (
								<Button
									color="primary"
									disableElevation
									variant={"contained"}
									startIcon={<Icon> checkIcon</Icon>}
									disabled={isLoading}
									onClick={() => {
										formRef.current?.submitForm();
									}}
								>
									{isLoading ? "Processando..." : "Confirmar"}
								</Button>
							) : (
								""
							)}
						</DialogActions>
					</Box>
				</Modal>
			)}
		</div>
	);
};
